import { isIOS, isMobile } from '@src/utils';

/**
 * Convert HTML to RTF with Arabic support
 * Supports: bold, italic, underline, paragraphs, tables, RTL
 */
export function htmlToRtf(html: string) {
  // Enhanced Unicode support with proper font table for Arabic
  let rtf =
    '{\\rtf1\\fbidis\\ansi\\ansicpg65001\\uc1\\deff0\\deflang1025\n{\\fonttbl{\\f0\\fnil\\fcharset0 Tahoma;}}\n{\\*\\generator NotesExporter;}\n';
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;

  // Function to escape Unicode characters for RTF (properly handles Arabic)
  function escapeUnicode(text: string): string {
    let result = '';
    for (let i = 0; i < text.length; i++) {
      const char = text.charAt(i);
      const code = char.charCodeAt(0);

      // For ASCII characters, keep as is
      if (code <= 127) {
        result += char;
      } else {
        // For Unicode characters (including Arabic), use RTF Unicode escape
        result += `\\u${code}?`;
      }
    }
    return result;
  }

  // Convert a DOM node to RTF format
  function convertNodeToRtf(node: Node): string {
    if (node.nodeType === Node.TEXT_NODE) {
      // Preserve Arabic correctly with Unicode escaping
      const textContent = node.textContent?.replace(/\n/g, '\\line ') ?? '';
      return escapeUnicode(textContent);
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return '';

    const el = node as HTMLElement;
    let start = '',
      end = '';

    switch (el.tagName.toLowerCase()) {
      case 'b':
      case 'strong':
        start = '\\b ';
        end = '\\b0 ';
        break;
      case 'i':
      case 'em':
        start = '\\i ';
        end = '\\i0 ';
        break;
      case 'u':
        start = '\\ul ';
        end = '\\ul0 ';
        break;
      case 'br':
        return '\\line ';
      case 'p':
        start = '\\rtlpar ';
        end = '\\par ';
        break;
      case 'table':
        start = '';
        end = '';
        break;
      case 'tr':
        start = '';
        end = '\\row ';
        break;
      case 'td':
        const width = el.offsetWidth || 100;
        start = `\\cellx${width} `;
        end = '';
        break;
    }

    let children = '';
    node.childNodes.forEach((child) => {
      children += convertNodeToRtf(child);
    });

    return start + children + end;
  }

  tempDiv.childNodes.forEach((child) => {
    rtf += convertNodeToRtf(child) + '\\line ';
  });

  rtf += '}';
  return rtf;
}

/**
 * Trigger file download with appropriate timeout based on device type
 */
export function triggerFileDownload(url: string, name: string) {
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();

  // Use longer timeout for mobile devices to prevent empty downloads
  const isMobileDevice = isIOS() || isMobile(navigator.userAgent);
  const timeout = isMobileDevice ? 5000 : 100;

  setTimeout(() => {
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, timeout);
}
